// Single source of the landing page's facts, and the switchboard for sections
// that are built but not yet showable.
//
// Every figure and list here comes from the CivicLens prospectus (September
// 2026, "CivicLens_Prospectus_2026-2028"). Update them together when the
// prospectus is refreshed so the page never shows a mix of vintages.
//
// PENDING flags gate sections whose *content* we do not yet have permission or
// material for (logos, quotes, press). The sections exist and render nothing
// until their flag is flipped; docs/civic-lens-landing-page.md lists what each
// one needs. Flip a flag only once the content next to it is real.

export const PENDING = {
  // Member organisations. The prospectus names them, but showing a logo on a
  // marketing page needs each organisation's permission and an SVG mark.
  memberLogos: false,
  // Funders: Knight, Omidyar, Packard, Reset, Craig Newmark. Same permission
  // and asset requirement as members.
  funderLogos: false,
  // Quotes from working journalists / researchers, attributed by name and role.
  testimonials: false,
  // Front-page stories the platform contributed to, with outlet and link.
  press: false,
  // A product visual in the hero — either a curated screenshot or a live
  // sample of public posts. Decision + asset/endpoint needed.
  heroVisual: false,
  // Public system-status page (roadmap 2026 Q3). No URL yet.
  statusPage: false,
  // The roadmap section is built (RoadmapSection) but held back until there is
  // a decision on committing to it publicly (Cameron, 2026-09-18).
  roadmap: false,
}

export const FIGURES_AS_OF = 'September 2026'

// Order is deliberate: the figure that says "scale" first, the one that says
// "current" last.
export const FIGURES = [
  { value: '~1B', label: 'posts in the database' },
  { value: '20M+', label: 'accounts represented' },
  { value: '250K+', label: 'accounts tracked every day' },
  { value: '300M+', label: 'posts collected in the last 12 months' },
]

export const PLATFORM_GROUPS = [
  {
    name: 'Mainstream',
    platforms: ['Facebook', 'Instagram', 'TikTok', 'X', 'YouTube', 'LinkedIn', 'Threads', 'Bluesky', 'Snapchat'],
  },
  {
    name: 'Alternative',
    platforms: ['Truth Social', 'Rumble', 'Telegram', 'Gab', 'Gettr', 'Weibo', 'Line', 'VK'],
  },
  {
    name: 'Long-form',
    platforms: ['Podcasts', 'Substack'],
  },
]

export const PLATFORM_COUNT = PLATFORM_GROUPS.reduce((n, g) => n + g.platforms.length, 0)

// The four communities the prospectus names as paying members today.
export const AUDIENCES = [
  {
    key: 'newsrooms',
    heading: 'Newsrooms',
    body: 'Reporters and editors tracking narratives, verifying what spread and where, and reconstructing how a post went viral — on deadline.',
  },
  {
    key: 'universities',
    heading: 'Universities',
    body: 'Research groups that need a complete, consistent corpus across platforms as the backbone for large-scale studies.',
  },
  {
    key: 'advocacy',
    heading: 'Advocacy and civil society',
    body: 'Human-rights, election-integrity and public-interest organisations monitoring the conversation around the issues they work on.',
  },
  {
    key: 'applied',
    heading: 'Applied research and analytics',
    body: 'Teams that build their own analysis and reporting on top of the collected data through the API and exports.',
  },
]

// Roadmap phases, condensed from the prospectus timeline. "Where it is going",
// not a commitment — the prospectus itself says timing shifts with funding.
export const ROADMAP = [
  {
    when: '2026 · Q3',
    heading: 'Support and openness',
    body: 'Customer support and documentation. API streamlining and a public status page. Comment collection begins on a subset of posts.',
  },
  {
    when: '2026 · Q4',
    heading: 'Faster signal',
    body: 'Time-aware overperformance scoring. Caption and image-text processing. Actor-set consolidation with custom tagging. First follower-graph network analysis.',
  },
  {
    when: '2027',
    heading: 'Everything connected',
    body: 'Media matching by perceptual hash, so one clip can be traced everywhere it appears. Fuller network analysis. In-platform AI labelling and narrative detection. Near-real-time collection for high-priority accounts.',
  },
  {
    when: '2028',
    heading: 'A million accounts',
    body: 'Collection breadth and frequency scaled toward a million tracked accounts. Deeper automation and agent integration. Hardened reliability and new sources.',
  },
]

export const CONTACT_EMAIL = 'support@civiclens.dev'
